import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, AlertCircle, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [institutionCode, setInstitutionCode] = useState('');
  const [showInstitutionCode, setShowInstitutionCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const credentials = { email, password };
      // Add institution_code only if provided
      if (institutionCode.trim()) {
        credentials.institution_code = institutionCode.trim().toLowerCase();
      }
      
      const userData = await login(credentials);
      
      // Check if subscription is expired - redirect to subscription page
      if (userData.subscription_expired) {
        toast.warning('Your trial has expired. Please subscribe to continue.');
        navigate('/subscription');
        return;
      }
      
      toast.success('Welcome to HMC Buddy!');
      
      // Check if user is platform admin (no institution_id)
      if (!userData.institution_id && userData.role === 'super_admin') {
        // Platform admin goes to Platform Dashboard
        navigate('/platform');
      } else {
        // Institution users go to regular Dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="/healventure-logo.png" 
              alt="HMC Buddy" 
              className="w-16 h-16 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">HMC Buddy</h1>
              <p className="text-sm text-slate-600">Hospital Management System</p>
            </div>
          </div>
          
          <h2 className="text-4xl font-bold text-slate-900 font-[Manrope] mb-4">
            Hospital Information Management System
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Streamline patient care, manage records, and optimize hospital operations with our comprehensive management solution.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-red-100">
              <p className="text-2xl font-bold text-red-500">24/7</p>
              <p className="text-sm text-slate-600">Patient Care</p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-red-100">
              <p className="text-2xl font-bold text-red-500">100%</p>
              <p className="text-sm text-slate-600">Data Security</p>
            </div>
          </div>
        </div>
      </div>

      <div className="login-right">
        <Card className="login-form border-0 shadow-xl">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold font-[Manrope]">Welcome back</CardTitle>
            <CardDescription>Enter your credentials to access your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@hospital.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-password"
                  />
                </div>
              </div>
              
              {/* Institution Code Toggle */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowInstitutionCode(!showInstitutionCode)}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <Building2 className="w-4 h-4" />
                  Multi-tenant login (institution code)
                  {showInstitutionCode ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {showInstitutionCode && (
                  <div className="mt-3 space-y-2">
                    <Label htmlFor="institutionCode">Institution Code</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="institutionCode"
                        type="text"
                        placeholder="e.g., city-medical"
                        value={institutionCode}
                        onChange={(e) => setInstitutionCode(e.target.value)}
                        className="pl-10"
                        data-testid="login-institution-code"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      Leave empty if logging in as platform admin
                    </p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-red-500 hover:bg-red-600 rounded-full"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
              
              <div className="text-center pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  New institution?{' '}
                  <Link to="/institution-signup" className="text-red-500 hover:text-red-600 font-medium">
                    Register here
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
